import React, { useEffect, useRef } from "react";
import { BusinessInfo, BusinessInfoError } from "../../types/storesetting";
import { Upload, X, Info, FileText, Image as ImageIcon } from "lucide-react";
import { Input } from "../ui/input2";
import { Textarea } from "../ui/Textarea";
import SectionHeader from "../ui/SectionHeader";
import { Button } from "../ui/Button";
import InfoContainer from "../ui/InfoContainer";
import WebsiteSectionCard from "../ui/WebsiteSectionCard";

interface BusinessInfoSectionProps {
  businessInfo: BusinessInfo;
  onChange: (updated: BusinessInfo) => void;
  onValidChange?: (isValid: boolean) => void;
  errors: BusinessInfoError;
  setErrors: React.Dispatch<React.SetStateAction<BusinessInfoError>>;
  handleContinue: () => void;
  existingImageUrl?: string | null;
  onClearExistingImage?: () => void;
}

const MAX_DESC = 1000;

const BusinessInfoSection: React.FC<BusinessInfoSectionProps> = ({
  businessInfo,
  onChange,
  onValidChange,
  errors,
  setErrors,
  handleContinue,
  existingImageUrl,
  onClearExistingImage
}) => {
  const charCount = businessInfo.description?.length ?? 0;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const hasImage = businessInfo.image instanceof File || !!existingImageUrl;

    const isValid =
      businessInfo.heading?.trim() !== "" &&
      businessInfo.subheading?.trim() !== "" &&
      charCount >= 150 &&
      hasImage;

    onValidChange?.(isValid);
  }, [businessInfo, charCount, onValidChange, existingImageUrl]);

  const handleChange = (field: keyof BusinessInfo, value: string | File | null) => {
    onChange({ ...businessInfo, [field]: value });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleChange("image", file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleChange("image", file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

  const previewData = React.useMemo(() => {
    if (businessInfo.image instanceof File) {
      return {
        src: URL.createObjectURL(businessInfo.image),
        isExisting: false
      };
    }
    if (existingImageUrl) {
      return {
        src: existingImageUrl,
        isExisting: true
      };
    }
    return null;
  }, [businessInfo.image, existingImageUrl]);

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <WebsiteSectionCard
        title="About Us Content"
        icon={FileText}
      >
        <div className="space-y-6">
          {/* Text Content Section */}
          <div>
            <SectionHeader title="Business Information" />

            <InfoContainer
              message="Provide details about your business to display in the About section"
              iconSize={13}
              className="mb-4"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Heading <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Welcome to Our Salon"
                  value={businessInfo.heading}
                  onChange={(e) => handleChange("heading", e.target.value)}
                  className={`w-full ${errors.heading ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                />
                {errors.heading && <p className="text-red-500 text-xs mt-1.5">{errors.heading}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Sub Heading <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Where beauty meets relaxation"
                  value={businessInfo.subheading}
                  onChange={(e) => handleChange("subheading", e.target.value)}
                  className={`w-full ${errors.subheading ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
                />
                {errors.subheading && <p className="text-red-500 text-xs mt-1.5">{errors.subheading}</p>}
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <Textarea
                placeholder="Tell your story here (min 150 characters)..."
                value={businessInfo.description}
                onChange={(e) => handleChange("description", e.target.value.slice(0, MAX_DESC))}
                className={`w-full min-h-[150px] resize-y ${errors.description ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""}`}
              />
              <div className="flex justify-between items-center mt-1.5">
                <span className={`text-xs ${charCount >= 150 ? "text-green-600 font-medium" : "text-red-500"}`}>
                  {charCount >= 150 ? "✓ Minimum length met" : `Need ${150 - charCount} more characters`}
                </span>
                <span className="text-xs text-gray-500 font-mono">{charCount}/{MAX_DESC}</span>
              </div>
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>
          </div>

          {/* Image Section */}
          <div>
            <SectionHeader title="Business Image" />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                About Image <span className="text-red-500">*</span>
              </label>

              <div
                className={`relative border-2 border-dashed rounded-xl p-4 h-48 flex flex-col items-center justify-center text-center transition-all cursor-pointer group bg-gray-50 hover:bg-white ${errors.image
                  ? "border-red-300 hover:border-red-500 bg-red-50/30"
                  : "border-gray-300 hover:border-[#885ABB]"
                  }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                {previewData ? (
                  <div className="relative w-full h-full rounded-lg border border-gray-200 shadow-sm flex items-center justify-center bg-white">
                    <img
                      src={previewData.src}
                      alt="Business Preview"
                      className="max-h-full max-w-full object-contain rounded-lg"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (previewData.isExisting) {
                          onClearExistingImage?.();
                        } else {
                          handleChange("image", null);
                        }
                      }}
                      className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md transition-all duration-200 hover:scale-110 z-10 opacity-0 group-hover:opacity-100"
                      title="Remove image"
                    >
                      <X size={14} />
                    </button>
                    {!previewData.isExisting && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] py-1 text-center opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg">
                        Click to replace
                      </div>
                    )}
                    {previewData.isExisting && (
                      <div className="absolute bottom-0 left-0 right-0 bg-blue-500/80 text-white text-[10px] py-1 text-center rounded-b-lg">
                        Current Image (Click to replace)
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center pointer-events-none">
                    <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Upload className="text-[#885ABB]" size={24} />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-0.5">
                      Click to upload image
                    </p>
                    <p className="text-xs text-gray-400">
                      or drag and drop your file here
                    </p>
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
              {errors.image && <p className="text-red-600 text-xs mt-2 flex items-center gap-1"><Info size={12} /> {errors.image}</p>}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-4 border-t border-gray-100 flex justify-end">
            <Button
              onClick={handleContinue}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </WebsiteSectionCard>
    </div>
  );
};

export default React.memo(BusinessInfoSection);
